import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { 
  FileText, Users, Car, Package, DollarSign, TrendingUp, TrendingDown, 
  AlertCircle, CheckCircle, Clock, Wrench, ShoppingCart, CreditCard,
  UserCheck, Building, Calendar, Bell, BarChart3
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/dateFormat';
import { useOrganization } from '../hooks/useOrganization';

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      inquiry: 0,
      pendingApproval: 0,
      ongoing: 0,
      pendingEntries: 0
    },
    revenueData: [],
    distributionData: [],
    customers: {
      total: 0,
      newThisMonth: 0,
      returning: 0
    },
    inventory: {
      totalParts: 0,
      lowStock: 0,
      outOfStock: 0,
      totalValue: 0
    },
    financial: {
      totalRevenue: 0,
      pendingInvoices: 0,
      unpaidInvoices: 0,
      partialInvoices: 0,
      paidInvoices: 0,
      overdueInvoices: 0,
      totalQuotations: 0,
      totalPaidAmount: 0
    },
    operations: {
      totalVehicles: 0,
      activeTechnicians: 0,
      totalBranches: 0,
      serviceReminders: 0
    },
    crm: {
      totalLeads: 0,
      activeDeals: 0,
      conversionRate: 0,
      totalTasks: 0
    },
    recentActivities: [],
    topPerformers: [],
    alerts: []
  });
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    period: 'monthly',
    branch: 'all'
  });

  const fetchBranches = async () => {
    try {
      const response = await axiosInstance.get('/api/branches/all');
      if (response.data) {
        setBranches(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      setBranches([]);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.branch !== 'all') params.append('branchId', filters.branch);
      params.append('period', filters.period);

      const response = await axiosInstance.get(`/api/dashboard/unified?${params}`);
      const data = response.data?.data || {};

      // Generate alerts
      const alerts = [
        ...(data.inventory?.outOfStock > 0 ? [{ type: 'error', message: `${data.inventory.outOfStock} items out of stock` }] : []),
        ...(data.inventory?.lowStock > 0 ? [{ type: 'warning', message: `${data.inventory.lowStock} items low in stock` }] : []),
        ...(data.financial?.pendingInvoices > 10 ? [{ type: 'info', message: `${data.financial.pendingInvoices} pending invoices need attention` }] : [])
      ];

      setDashboardData({
        metrics: {
          inquiry: data.jobCards?.total || 0,
          pendingApproval: data.jobCards?.pending || 0,
          ongoing: data.jobCards?.inProgress || 0,
          pendingEntries: data.jobCards?.completed || 0
        },
        revenueData: data.revenue?.revenueData || [],
        distributionData: data.revenue?.distributionData || [],
        customers: data.customers || { total: 0, newThisMonth: 0, returning: 0 },
        inventory: {
          totalParts: data.inventory?.totalItems || 0,
          lowStock: data.inventory?.lowStock || 0,
          outOfStock: data.inventory?.outOfStock || 0,
          totalValue: data.inventory?.totalValue || 0
        },
        financial: {
          totalRevenue: data.revenue?.totalRevenue || 0,
          pendingInvoices: data.financial?.pendingInvoices || 0,
          unpaidInvoices: data.financial?.unpaidInvoices || 0,
          partialInvoices: data.financial?.partialInvoices || 0,
          paidInvoices: data.financial?.paidInvoices || 0,
          overdueInvoices: data.financial?.overdueInvoices || 0,
          totalQuotations: data.financial?.totalQuotations || 0,
          totalPaidAmount: data.financial?.totalPaidAmount || 0
        },
        operations: {
          totalVehicles: data.vehicles?.total || 0,
          activeTechnicians: 0,
          totalBranches: 1,
          serviceReminders: 0
        },
        crm: data.crm || { totalLeads: 0, activeDeals: 0, conversionRate: 0, totalTasks: 0 },
        recentActivities: data.recentActivities || [],
        topPerformers: [],
        alerts
      });
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't set default dates - show all data
    setFilters(prev => ({
      ...prev,
      fromDate: '',
      toDate: ''
    }));
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const handleSubmit = () => {
    fetchDashboardData();
  };

  const { organization } = useOrganization();

  const metricCards = [
    { title: 'Total Job Cards', value: dashboardData.metrics.inquiry, color: 'bg-blue-500', textColor: 'text-blue-600', icon: FileText, trend: '+12%' },
    { title: 'Pending Approval', value: dashboardData.metrics.pendingApproval, color: 'bg-yellow-500', textColor: 'text-yellow-600', icon: Clock, trend: '-5%' },
    { title: 'In Progress', value: dashboardData.metrics.ongoing, color: 'bg-green-500', textColor: 'text-green-600', icon: Wrench, trend: '+8%' },
    { title: 'Completed', value: dashboardData.metrics.pendingEntries, color: 'bg-purple-500', textColor: 'text-purple-600', icon: CheckCircle, trend: '+15%' }
  ];

  const businessMetrics = [
    { title: 'Total Revenue', value: formatCurrency(dashboardData.financial.totalRevenue, organization?.currency_code), color: 'bg-emerald-500', textColor: 'text-emerald-600', icon: DollarSign },
    { title: 'Total Customers', value: dashboardData.customers.total, color: 'bg-indigo-500', textColor: 'text-indigo-600', icon: Users },
    { title: 'Total Vehicles', value: dashboardData.operations.totalVehicles, color: 'bg-orange-500', textColor: 'text-orange-600', icon: Car },
    { title: 'Inventory Items', value: dashboardData.inventory.totalParts, color: 'bg-teal-500', textColor: 'text-teal-600', icon: Package }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" key="dashboard-content">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
      </div>

      {/* Filters */}
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => setFilters({...filters, fromDate: e.target.value})}
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => setFilters({...filters, toDate: e.target.value})}
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
            <select
              value={filters.period}
              onChange={(e) => setFilters({...filters, period: e.target.value})}
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
            <select
              value={filters.branch}
              onChange={(e) => setFilters({...filters, branch: e.target.value})}
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            >
              <option value="all">All Branches</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
          <button 
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-3 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 touch-target"
          >
            {loading ? 'Loading...' : 'Submit'}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {dashboardData.alerts.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            System Alerts
          </h3>
          <div className="space-y-2">
            {dashboardData.alerts.map((alert, index) => (
              <div key={index} className={`p-3 rounded-lg ${
                alert.type === 'error' ? 'bg-red-50 text-red-800' :
                alert.type === 'warning' ? 'bg-yellow-50 text-yellow-800' :
                'bg-blue-50 text-blue-800'
              }`}>
                {alert.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Job Cards Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {metricCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className={`text-3xl font-bold ${card.textColor} mt-2`}>{card.value}</p>
                  {card.trend && (
                    <p className={`text-sm mt-1 flex items-center gap-1 ${
                      card.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {card.trend.startsWith('+') ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {card.trend}
                    </p>
                  )}
                </div>
                <div className={`w-12 h-12 ${card.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Business Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {businessMetrics.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className={`text-2xl font-bold ${card.textColor} mt-2`}>{card.value}</p>
                </div>
                <div className={`w-12 h-12 ${card.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Revenue Analysis */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Revenue Analysis
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dashboardData.revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(Number(value), organization?.currency_code)} />
              <Area dataKey="revenue" fill="#3b82f6" fillOpacity={0.3} stroke="#3b82f6" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Financial Overview */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Financial Overview
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Revenue</span>
              <span className="text-lg font-semibold text-green-600">
                {formatCurrency(dashboardData.financial.totalRevenue, organization?.currency_code)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Unpaid Invoices</span>
              <span className="text-lg font-semibold text-red-600">{dashboardData.financial.unpaidInvoices}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Partial Paid</span>
              <span className="text-lg font-semibold text-yellow-600">{dashboardData.financial.partialInvoices}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Paid Invoices</span>
              <span className="text-lg font-semibold text-green-600">{dashboardData.financial.paidInvoices}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Quotations</span>
              <span className="text-lg font-semibold text-blue-600">{dashboardData.financial.totalQuotations}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Inventory Status */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Inventory Status
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Parts</span>
              <span className="text-lg font-semibold">{dashboardData.inventory.totalParts}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Low Stock</span>
              <span className="text-lg font-semibold text-yellow-600">{dashboardData.inventory.lowStock}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Out of Stock</span>
              <span className="text-lg font-semibold text-red-600">{dashboardData.inventory.outOfStock}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Value</span>
              <span className="text-lg font-semibold text-green-600">
                {formatCurrency(dashboardData.inventory.totalValue, organization?.currency_code)}
              </span>
            </div>
          </div>
        </div>

        {/* Operations */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building className="w-5 h-5" />
            Operations
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Vehicles</span>
              <span className="text-lg font-semibold">{dashboardData.operations.totalVehicles}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active Technicians</span>
              <span className="text-lg font-semibold text-blue-600">{dashboardData.operations.activeTechnicians}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Branches</span>
              <span className="text-lg font-semibold">{dashboardData.operations.totalBranches}</span>
            </div>
          </div>
        </div>

        {/* Customer Insights */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserCheck className="w-5 h-5" />
            Customer Insights
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Customers</span>
              <span className="text-lg font-semibold">{dashboardData.customers.total}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">New This Month</span>
              <span className="text-lg font-semibold text-green-600">{dashboardData.customers.newThisMonth}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Returning</span>
              <span className="text-lg font-semibold text-blue-600">{dashboardData.customers.returning}</span>
            </div>
          </div>
        </div>

        {/* CRM Performance */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            CRM Performance
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Leads</span>
              <span className="text-lg font-semibold">{dashboardData.crm.totalLeads}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active Deals</span>
              <span className="text-lg font-semibold text-green-600">{dashboardData.crm.activeDeals}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Conversion Rate</span>
              <span className="text-lg font-semibold text-blue-600">{dashboardData.crm.conversionRate}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      {dashboardData.recentActivities.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Recent Activities
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Job #</th>
                  <th className="text-left py-2">Vehicle</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-left py-2">Technician</th>
                  <th className="text-left py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.recentActivities.slice(0, 5).map((activity, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-2">{activity.jobNumber}</td>
                    <td className="py-2">{activity.vehicle}</td>
                    <td className="py-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        activity.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        activity.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {activity.status}
                      </span>
                    </td>
                    <td className="py-2">{activity.technician}</td>
                    <td className="py-2">{formatDate(activity.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Revenue Distribution */}
      {dashboardData.distributionData.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Revenue Distribution
          </h3>
          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={dashboardData.distributionData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({name, value}) => `${name}: ${value}%`}
                >
                  {dashboardData.distributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
